// Basic
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
// Store and actions
import actionsPlans from '../../../api/actionsPlans';
import useRootOrganizationStore from '@/app/store/rootOrganization';
// Common
import getInitialValueForRowSelected from '@/common-utils/components/renders/dataTable/utils/GetInitialValueForRowSelected';
import { useToast } from '@/common-utils/hooks/use-toast';
// Components
import Entity from '@/common-utils/components/renders/entity/Entity';
import OrganizationsTable from '@/app/modules/organizations/components/tables/OrganizationsTable';
import HeaderTitle from '@/common-utils/components/renders/modal/components/headers/HeaderTitle/HeaderTitle';
import FooterForm from '@/common-utils/components/renders/modal/components/footers/FooterForm/FooterForm';
import { Modal, ModalBody, ModalContent } from '@/common-utils/components/ui/modal';
// Lang
import { nsPlan } from '@/app/i18n/namespaces';
import setFiltersEventName from '@/common-utils/components/renders/dataTable/utils/setFiltersEventName';

/**
 * @param root0
 * @param root0.isOpen
 * @param root0.setIsOpen
 * @param root0.setLaunchRefetch
 *  @returns {JSX} ModalAddAllowedClients
 */
function ModalAddAllowedClients({ isOpen, setIsOpen, setLaunchRefetch }) {

    // Store
    const rootOrganization = useRootOrganizationStore(s => s.rootOrganization);

    // URL Params
    const { planId } = useParams();

    // State
    const [disableAction, setDisableAction] = useState(true);
    const [clientSelected, setClientSelected] = useState(getInitialValueForRowSelected());

    // Toast
    const { toast } = useToast();

    // Hooks
    useEffect(() => {
        setDisableAction(!Object.keys(clientSelected.rowIds).length);
    }, [clientSelected]);

    // Api calls
    const onSuccess = () => {
        setIsOpen(false);
        setLaunchRefetch(true);
        toast({
            description: <Entity namespace={nsPlan} entity='modal.add_allowed_clients.success' />,
            variant: 'success'
        });
    };
    const onError = (error) => {
        const errorCode = error.response?.data?.errorCode ?? error.code;
        const addClientErrorCode = errorCode === 'CLIENT_ALREADY_ALLOWED' ? 'CLIENT_ALREADY_ALLOWED' : 'UNKNOWN_ERROR';
        toast({
            description: <Entity namespace={nsPlan} entity={`toast.error.${addClientErrorCode}`} />,
            variant: 'danger'
        });
    };

    const addAllowedClients = actionsPlans.addAllowedClients(onSuccess, onError);

    // Functions
    const handleValidation = () => {
        addAllowedClients.mutate({
            organizationId: rootOrganization.uuid,
            planId: planId,
            dataToSend: clientSelected.rowIds && Object.keys(clientSelected.rowIds)
        });
    };

    return (
        <Modal open={isOpen} onOpenChange={setIsOpen}>
            <ModalContent className='max-w-6xl'>
                <HeaderTitle title='modal.add_allowed_clients.title' namespace={nsPlan} />
                <ModalBody className='modal-max-height overflow-auto'>
                    <OrganizationsTable
                        filtersEventName={setFiltersEventName('organizations', true)}
                        singleSelection={false}
                        rowsSelected={clientSelected}
                        setRowsSelected={setClientSelected}
                    />
                </ModalBody>
                <FooterForm
                    onSubmit={handleValidation}
                    isSubmitting={addAllowedClients.isLoading}
                    isSubmitDisabled={disableAction}
                    showSubmitButton={true}
                />
            </ModalContent>
        </Modal>
    );
}

ModalAddAllowedClients.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    setIsOpen: PropTypes.func.isRequired,
    setLaunchRefetch: PropTypes.func
};

export default ModalAddAllowedClients;